'use client';

import { useState } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { User, Menu, X, Leaf, LogOut, Settings, ChevronDown, ArrowRight, LayoutDashboard } from 'lucide-react';
import { Button } from '@myapp/ui/components/button';
import { Badge } from '@myapp/ui/components/badge';
import { useAuth } from './auth/MockAuthProvider';


export function AppHeader() {
  const router = useRouter();
  const { user, logout } = useAuth();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);

  const handleLoginClick = () => {
    router.push('/login');
    setIsMenuOpen(false);
  };

  const handleLogout = () => {
    logout();
    setIsUserMenuOpen(false);
  };

  return (
    <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-sm border-b border-gray-100">
      <div className="container mx-auto px-4 py-4">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link href="/" className="flex items-center gap-2">
            <div className="w-8 h-8 bg-gradient-to-r from-green-500 to-blue-500 rounded-lg flex items-center justify-center">
              <Leaf className="w-5 h-5 text-white" />
            </div>
            <div className="font-bold text-xl text-gray-900">
              강원<span className="text-green-600">푸드핏</span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-8">
            <Link href="/seasonal" className="text-gray-700 hover:text-green-600 transition-colors">
              제철 특산물
            </Link>
            <Link href="/recipes" className="text-gray-700 hover:text-green-600 transition-colors">
              AI 레시피
            </Link>
            <Link href="/farmers" className="text-gray-700 hover:text-green-600 transition-colors">
              농가 스토리
            </Link>
            <Link href="/about" className="text-gray-700 hover:text-green-600 transition-colors">
              소개
            </Link>
            {user && (user.role === 'farmer' || user.role === 'admin') && (
              <Link href="/dashboard" className="text-gray-700 hover:text-green-600 transition-colors flex items-center gap-1">
                <LayoutDashboard className="w-4 h-4" />
                대시보드
              </Link>
            )}
          </nav>

          {/* User Actions */}
          <div className="flex items-center gap-4">
            {user ? (
              <div className="relative">
                <Button 
                  variant="ghost" 
                  className="flex items-center gap-2 hover:bg-gray-100 hidden md:flex"
                  onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                >
                  <div className="w-8 h-8 rounded-full bg-gradient-to-r from-green-500 to-blue-500 flex items-center justify-center text-white text-lg">
                    {user.avatar}
                  </div>
                  <div className="text-left">
                    <div className="text-sm font-medium">{user.name}</div>
                    {user.type === 'farmer' && (
                      <div className="text-xs text-gray-500">{user.farmName}</div>
                    )}
                  </div>
                  <ChevronDown className="w-4 h-4" />
                </Button>

                {/* User Dropdown Menu */}
                {isUserMenuOpen && (
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50">
                    <Link href="/profile" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      <div className="flex items-center gap-2">
                        <Settings className="w-4 h-4" />
                        프로필 설정
                      </div>
                    </Link>
                    {user.type === 'farmer' && (
                      <Link href="/dashboard" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                        <div className="flex items-center gap-2">
                          <User className="w-4 h-4" />
                          농가 대시보드
                        </div>
                      </Link>
                    )}
                    <hr className="my-1" />
                    <button 
                      onClick={handleLogout}
                      className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    >
                      <div className="flex items-center gap-2">
                        <LogOut className="w-4 h-4" />
                        로그아웃
                      </div>
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <Button 
                onClick={handleLoginClick}
                className="bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 hidden md:flex"
              >
                구글 로그인
              </Button>
            )}

            {/* Mobile Menu Button */}
            <Button
              variant="ghost"
              size="sm"
              className="md:hidden"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
            >
              {isMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </Button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="md:hidden mt-4 pb-4 border-t border-gray-100">
            <nav className="flex flex-col gap-4 pt-4">
              <Link href="/seasonal" className="text-gray-700 hover:text-green-600 transition-colors">
                제철 특산물
              </Link>
              <Link href="/recipes" className="text-gray-700 hover:text-green-600 transition-colors">
                AI 레시피
              </Link>
              <Link href="/farmers" className="text-gray-700 hover:text-green-600 transition-colors">
                농가 스토리
              </Link>
              <Link href="/about" className="text-gray-700 hover:text-green-600 transition-colors">
                소개
              </Link>
              {user && (user.role === 'farmer' || user.role === 'admin') && (
                <Link href="/dashboard" className="text-gray-700 hover:text-green-600 transition-colors flex items-center gap-1">
                  <LayoutDashboard className="w-4 h-4" />
                  대시보드
                </Link>
              )}
              
              <div className="pt-4 border-t border-gray-100">
                {user ? (
                  <div className="flex flex-col gap-3">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-gradient-to-r from-green-500 to-blue-500 flex items-center justify-center text-white text-lg">
                        {user.avatar}
                      </div>
                      <div>
                        <div className="text-sm font-medium">{user.name}</div>
                        {user.role === 'farmer' && (
                          <div className="text-xs text-gray-500">농업인</div>
                        )}
                        {user.role === 'admin' && (
                          <div className="text-xs text-gray-500">관리자</div>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex flex-col gap-2">
                      <Link href="/profile" className="text-sm text-gray-700 flex items-center gap-2">
                        <Settings className="w-4 h-4" />
                        프로필 설정
                      </Link>
                      {(user.role === 'farmer' || user.role === 'admin') && (
                        <Link href="/dashboard" className="text-sm text-gray-700 flex items-center gap-2">
                          <LayoutDashboard className="w-4 h-4" />
                          대시보드
                        </Link>
                      )}
                      <button 
                        onClick={handleLogout}
                        className="text-sm text-gray-700 flex items-center gap-2 text-left"
                      >
                        <LogOut className="w-4 h-4" />
                        로그아웃
                      </button>
                    </div>
                  </div>
                ) : (
                  <Button 
                    onClick={handleLoginClick}
                    className="bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 w-fit"
                  >
                    구글 로그인
                  </Button>
                )}
              </div>
            </nav>
          </div>
        )}

      </div>
    </header>
  );
}